// eslint-disable-next-line max-classes-per-file
import type CurrentAuthService from 'shared/services/current-auth.service';
import type LoginService from 'shared/login/login.service';
import {serializeUrlParams} from 'shared/utils/data-service-utils';
import {exists} from 'shared/utils/custom-utils';
import {assertIsNonEmptyString} from 'shared/utils/string-utils';

export class FileDownloadServiceProvider {
    public static providerName = 'fileDownloadService';

    public readonly $get: (_: CurrentAuthService, __: LoginService) => FileDownloadService;

    private baseUrl: string;

    constructor() {
        this.$get = /*@ngInject*/ (currentAuthService: CurrentAuthService, loginService: LoginService) => this.createFileDownloadService(currentAuthService, loginService);
    }

    public setBaseUrl(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    private createFileDownloadService(currentAuthService: CurrentAuthService, loginService: LoginService): FileDownloadService {
        return new FileDownloadService(currentAuthService, loginService, this.baseUrl);
    }
}

export class FileDownloadService {

    constructor(private currentAuthService: CurrentAuthService, private loginService: LoginService, private baseUrl: string) {
    }

    createRequest(url: string): FileDownloadRequest {
        assertIsNonEmptyString(url);
        const requestUrl = url.startsWith('/') ? `${this.baseUrl}${url}` : `${this.baseUrl}/${url}`;
        return new FileDownloadRequest(requestUrl, this.currentAuthService, this.loginService);
    }

    withoutBaseUrl(): FileDownloadService {
        return new FileDownloadService(this.currentAuthService, this.loginService, '');
    }
}

class FileDownloadRequest {
    private headers: Record<string, any> = {};
    private params: Record<string, any> = {};
    private loginRetried = false;

    /*@ngInject*/
    constructor(private baseUrl: string, private currentAuthService: CurrentAuthService, private loginService: LoginService) {
    }

    public withHeaders(headers: Record<string, any>): FileDownloadRequest {
        this.headers = headers;
        return this;
    }

    public withParams(params: Record<string, any>): FileDownloadRequest {
        this.params = params;
        return this;
    }

    public async downloadBlob(): Promise<Blob> {
        const url = this.createUrl();
        const response = await fetch(url, {
            method: 'GET',
            headers: this.headers
        });

        // If first request fails with Unauthorized, we try to log the user in and retry once
        if (response.status === 401 && !this.loginRetried) {
            const loginSuccess = await this.notLoggedInHandler();
            this.loginRetried = true;

            if (loginSuccess) {
                return this.downloadBlob();
            }
        }

        if (!response.ok) {
            throw new Error(`Failed to download file: ${response.statusText}`);
        }

        const contentType = response.headers.get('Content-Type') || 'application/octet-stream';
        const arrayBuffer = await response.arrayBuffer();
        return new Blob([arrayBuffer], {type: contentType});
    }

    public async download(downloadedFilename?: string | null): Promise<void> {
        const blob = await this.downloadBlob();
        const url = URL.createObjectURL(blob);

        // Extract the filename from the Content-Disposition header
        const response = await fetch(this.createUrl(), {
            method: 'HEAD', // Use HEAD request to fetch headers only
            headers: this.headers
        });

        let filename = downloadedFilename ?? 'downloaded-file';
        const contentDisposition = response.headers.get('Content-Disposition');
        if (exists(contentDisposition) && !exists(downloadedFilename)) {
            const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/i);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }

        // Create a temporary link and click it to trigger the download
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();

        // Cleanup
        URL.revokeObjectURL(url);
        document.body.removeChild(link);
    }

    private async notLoggedInHandler(): Promise<boolean> {
        const currentAuth = this.currentAuthService.getCurrentAuthValue();

        if (currentAuth.evided) {
            try {
                const newAuth = await this.loginService.relogin(true);
                return newAuth.evided;
            } catch {
                return false;
            }
        } else {
            try {
                const newAuth = await this.loginService.login();
                return newAuth.evided;
            } catch {
                return false;
            }
        }
    }

    private createUrl(): string {
        const serializedUrlParams = serializeUrlParams(this.params);

        if (serializedUrlParams.length === 0) {
            return this.baseUrl;
        }

        return this.baseUrl.includes('?')
            ? `${this.baseUrl}&${serializedUrlParams}`
            : `${this.baseUrl}?${serializedUrlParams}`;
    }
}